import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MessageCircle, ArrowRight, Diamond, Award, Sparkles, Shield } from 'lucide-react';

export const KolcoSBrilliantomRiga = () => {
  return (
    <>
      <Helmet>
        <title>Кольцо с бриллиантом в Риге | Бриллиантовые кольца | Pure Atelier</title>
        <meta name="description" content="Кольца с бриллиантами в Риге. Сертифицированные бриллианты GIA/IGI, лучшие цены в Латвии. Широкий выбор. Pure Atelier Riga." />
        <meta name="keywords" content="кольцо с бриллиантом Рига, бриллиантовые кольца, кольца с бриллиантами Латвия, diamond rings Riga" />
        <link rel="canonical" href="https://pureatelier.lv/kolco-s-brilliantom-riga" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/2735970/pexels-photo-2735970.jpeg?auto=compress&cs=tinysrgb&w=1920')] bg-cover bg-center opacity-10"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <span className="inline-block px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-medium mb-6">
                Pure Atelier Riga
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-stone-900 mb-6 leading-tight">
                Кольца с бриллиантами в Риге
              </h1>
              <p className="text-xl text-stone-600 mb-8 max-w-2xl mx-auto">
                Изысканные кольца с натуральными бриллиантами. Качество каждого бриллианта подтверждено международным сертификатом.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/gallery?category=rings">
                  <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8">
                    Смотреть коллекцию колец <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <a href="https://wa.me/37128448991?text=Здравствуйте!%20Интересует%20кольцо%20с%20бриллиантом." target="_blank" rel="noopener noreferrer">
                  <Button size="lg" variant="outline" className="border-amber-600 text-amber-700 hover:bg-amber-50 px-8">
                    <MessageCircle className="mr-2 h-5 w-5" /> Бесплатная консультация
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-8">
              {[
                { icon: Diamond, title: "Сертификаты GIA/IGI", desc: "Международное признание" },
                { icon: Award, title: "Ручная работа", desc: "Изготовлено в Латвии" },
                { icon: Sparkles, title: "Натуральные бриллианты", desc: "Высочайшее качество" },
                { icon: Shield, title: "Пожизненная гарантия", desc: "На все изделия" }
              ].map((item, i) => (
                <div key={i} className="text-center p-6">
                  <item.icon className="h-12 w-12 text-amber-600 mx-auto mb-4" />
                  <h3 className="font-semibold text-stone-900 mb-2">{item.title}</h3>
                  <p className="text-stone-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16 bg-stone-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-serif text-stone-900 mb-8 text-center">
                Кольца с бриллиантами от Pure Atelier
              </h2>
              
              <div className="prose prose-lg max-w-none text-stone-700">
                <p>
                  <strong>Pure Atelier</strong> предлагает широкий выбор колец с бриллиантами в Риге. В нашей коллекции 
                  представлены как классические кольца solitaire, так и современные дизайны с дополнительными бриллиантами.
                </p>
                
                <h3 className="text-2xl font-serif text-stone-900 mt-8 mb-4">Почему выбирают нас?</h3>
                <ul>
                  <li><strong>Сертифицированные бриллианты</strong> — каждый бриллиант имеет сертификат GIA или IGI</li>
                  <li><strong>Конкурентные цены</strong> — прямое сотрудничество без посредников</li>
                  <li><strong>Широкий выбор размеров</strong> — от 0.30ct до 2ct и более</li>
                  <li><strong>Различные металлы</strong> — белое золото, жёлтое золото, розовое золото, платина</li>
                </ul>

                <h3 className="text-2xl font-serif text-stone-900 mt-8 mb-4">Критерии качества бриллианта (4C)</h3>
                <p>
                  При выборе бриллианта мы руководствуемся системой 4C: <strong>Cut</strong> (огранка), 
                  <strong>Color</strong> (цвет), <strong>Clarity</strong> (чистота) и <strong>Carat</strong> (вес). 
                  Наши специалисты помогут подобрать оптимальное соотношение качества и цены.
                </p>
              </div>

              <div className="mt-12 text-center">
                <Link to="/gallery?category=rings">
                  <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8">
                    Смотреть все кольца с бриллиантами <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-stone-900 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-serif mb-4">Ищете идеальное кольцо с бриллиантом?</h2>
            <p className="text-stone-300 mb-8 max-w-2xl mx-auto">
              Свяжитесь с нами для получения персонального предложения и бесплатной консультации.
            </p>
            <a href="https://wa.me/37128448991?text=Здравствуйте!%20Интересует%20кольцо%20с%20бриллиантом." target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white px-8">
                <MessageCircle className="mr-2 h-5 w-5" /> Консультация в WhatsApp
              </Button>
            </a>
          </div>
        </section>
      </div>
    </>
  );
};

export default KolcoSBrilliantomRiga;
